import React, { useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Typography } from '@material-ui/core';
import { listAirings } from './apiQueries';
import { Airing } from '../../utils/types';

import AiringFilters, { AiringFiltersInput } from './AiringFilters';
import AiringsList from './AiringsList';

const Airings: React.FC<AiringsProps> = (props) => {
	const { channelId } = { ...props };
	const pageSize = 15;
	const initialPageSize = pageSize * 4;
	const sortDirection = 'DESC';
	const [page, setPage] = useState(0);
	const [count, setCount] = useState(0);
	const [filter, setFilters] = useState<AiringFiltersInput>({});

	const updateFilters = (newFilters: AiringFiltersInput): void => {
		setPage(0);
		setCount(0);
		setFilters(newFilters);
	};
	const { loading, error, data, fetchMore } = useQuery(listAirings, {
		variables: {
			nextToken: null,
			limit: initialPageSize,
			sortDirection,
			channelId,
			filter: Object.keys(filter).length > 0 ? filter : null
		}
	});

	const handleLoadMore = (): void => {
		const { nextToken } = data.listAirings;
		if (nextToken) {
			fetchMore({
				query: listAirings,
				variables: {
					nextToken,
					limit: pageSize,
					sortDirection,
					channelId,
					filter: Object.keys(filter).length > 0 ? filter : null
				},
				updateQuery: (previousResult: listAiringsData, { fetchMoreResult }): listAiringsData => {
					if (!fetchMoreResult) return previousResult;

					const previousItems = previousResult.listAirings.items;
					const newItems = fetchMoreResult.listAirings.items;
					const newNextToken = fetchMoreResult.listAirings.nextToken;

					setCount(previousItems.length + newItems.length);

					return {
						listAirings: {
							items: [...previousItems, ...newItems],
							nextToken: newNextToken,
							__typename: 'AiringConnection'
						}
					};
				}
			});
		}
	};

	const handleChangePage = (page: number): void => {
		handleLoadMore();
		setPage(page);
	};

	const orderByStartTime = (game1: Airing, game2: Airing): number => {
		return game2.startTime.localeCompare(game1.startTime);
	};

	const renderAiringList = (): JSX.Element => {
		if (loading) return <Typography>Loading...</Typography>;
		if (error) return <Typography>Error loading data from the server</Typography>;
		if (!data.listAirings || data.listAirings.items.length === 0) return <Typography>No airings found</Typography>;
		const currentPage = data.listAirings.items.slice(page * pageSize, page * pageSize + pageSize).sort(orderByStartTime);
		return (
			<AiringsList
				airings={currentPage}
				pageSize={pageSize}
				page={page}
				count={count || data.listAirings.items.length}
				handleChangePage={handleChangePage}
			/>
		);
	};

	return (
		<>
			<AiringFilters setGraphQLFilters={updateFilters} />
			{renderAiringList()}
		</>
	);
};

type listAiringsData = {
	listAirings: {
		items: Airing[];
		nextToken: string;
		__typename: 'AiringConnection';
	};
};

export type AiringsProps = {
	channelId?: string;
};

export default Airings;
